import React, { useCallback, useEffect, useState } from 'react';
import { parseApiDate } from '../utils/security';
import { fetchTsKayitNotlar, isaretleTsKayitNot } from '../services/tsKayitBasvuruService';
import TableListFooter from './TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

/**
 * Servis Onayları > Notlar: kayıt linkindeki "Sistem yöneticisine yaz" mesajları.
 */
const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const BASVURU_DURUM = {
  onay_bekliyor: 'Onay bekliyor',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
};

export default function TsKayitNotlarPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filtre, setFiltre] = useState('tumu');
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [islemId, setIslemId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchTsKayitNotlar({ sayfa: currentPage, limit: PAGE_DEFAULT_LIMIT, filtre });
      if (res?.basarili === false) {
        setError(res.mesaj || 'Notlar yüklenemedi');
        setItems([]);
        return;
      }
      setItems(res?.veri?.items || []);
      setMeta(normalizeSayfalama(res?.veri?.sayfalama));
    } catch (err) {
      setError(err?.message || 'Notlar yüklenemedi');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filtre]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtre]);

  const toggleOkundu = async (item) => {
    setIslemId(item.id);
    try {
      await isaretleTsKayitNot(item.id, !item.okundu);
      await load();
      onChange?.();
    } catch (err) {
      window.showNotification?.('error', err?.message || 'İşlem yapılamadı');
    } finally {
      setIslemId('');
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>
        <button
          type="button"
          className={`status-tab-btn ${filtre === 'tumu' ? 'active' : ''}`}
          onClick={() => setFiltre('tumu')}
        >
          Tümü
        </button>
        <button
          type="button"
          className={`status-tab-btn ${filtre === 'okunmadi' ? 'active' : ''}`}
          onClick={() => setFiltre('okunmadi')}
        >
          Okunmamış
        </button>
      </div>
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">Yükleniyor</div>
        </div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : items.length === 0 ? (
        <div className="empty-state" style={{ padding: 40, textAlign: 'center' }}>
          <i className="fas fa-envelope-open" style={{ fontSize: 32, color: 'var(--text-light)', marginBottom: 12 }} />
          <div style={{ color: 'var(--text-gray)', fontSize: 14 }}>Not yok</div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Gönderen</th>
                  <th>Teknik Servis</th>
                  <th>Mesaj</th>
                  <th className="islemler-sutunu">Durum</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} style={item.okundu ? { opacity: 0.7 } : { fontWeight: 500 }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatTarih(item.createdAt)}</td>
                    <td className="bo-dealer-cell">
                      <strong>{item.adSoyad || '—'}</strong>
                      {item.iletisim ? <small>{item.iletisim}</small> : null}
                      {item.kullaniciAdi ? <small>Kullanıcı adı: {item.kullaniciAdi}</small> : null}
                    </td>
                    <td className="bo-dealer-cell">
                      <strong>{item.techService?.ad || '—'}</strong>
                      {item.techService?.kod != null ? <small>#{item.techService.kod}</small> : null}
                      {item.basvuruDurum ? <small>Başvuru: {BASVURU_DURUM[item.basvuruDurum] || item.basvuruDurum}</small> : null}
                    </td>
                    <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 420 }}>{item.mesaj}</td>
                    <td className="islemler-sutunu">
                      <button
                        type="button"
                        className={item.okundu ? 'secondary-btn' : 'primary-btn'}
                        onClick={() => toggleOkundu(item)}
                        disabled={islemId === item.id}
                        title={item.okundu && item.okuyanKullanici ? `${item.okuyanKullanici.adSoyad || item.okuyanKullanici.kullaniciAdi} · ${formatTarih(item.okunmaTarihi)}` : undefined}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <i className={`fas ${item.okundu ? 'fa-undo' : 'fa-check'}`} />
                        <span>{item.okundu ? 'Okunmadı yap' : 'Okundu'}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={meta.total}
            currentPage={currentPage}
            totalPages={meta.totalPages}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
        </>
      )}
    </div>
  );
}
